<template>
	<v-menu offset-y :open-on-hover="isFullyPaid" :close-on-content-click="false">
		<template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
			<v-btn
				block
				small
				:depressed="!isPartiallyPaid"
				:outlined="isPartiallyPaid"
				:dark="!isPartiallyPaid"
				:style="btnStyle"
				class="d-flex align-center"
				style="pointer-events: auto !important; opacity: 1 !important; text-transform: none; letter-spacing: 0; justify-content: flex-start; padding: 0 10px;"
				v-bind="menuAttrs"
				v-on="menuOn"
			>
				<v-icon small left>{{ tooltipIcon }}</v-icon>
				<span class="caption font-weight-medium">{{ tooltipText }}</span>
			</v-btn>
		</template>

		<!-- Sin permiso (subcontratista) -->
		<div class="pa-3 white" style="max-width: 300px; min-width: 250px;" v-if="isSubcontractor">
			<div class="d-flex align-center mb-1" style="gap: 8px;">
				<v-icon small color="grey darken-1">mdi-credit-card-outline</v-icon>
				<p class="subtitle-2 font-weight-bold mb-0">Sin información</p>
			</div>
			<v-divider class="my-2"></v-divider>
			<p class="caption mb-0 grey--text text--darken-1">No tienes permisos para gestionar pagos.</p>
		</div>

		<!-- Opciones de pago (no pagado o pago parcial, no subcontratista) -->
		<div class="pa-3 white" style="max-width: 400px; min-width: 350px;" v-else-if="!isFullyPaid">
			<div class="d-flex align-center mb-1" style="gap: 8px;">
				<v-icon small style="color: #FF120B;">mdi-cash-clock</v-icon>
				<p class="subtitle-2 font-weight-bold mb-0">Gestionar pago de cuota</p>
			</div>
			<v-divider class="my-2"></v-divider>
			<p class="caption grey--text text--darken-1 mb-3">
				Registra un pago recibido o envía una solicitud de pago al cliente.
			</p>
			<v-btn
				block
				small
				outlined
				style="text-transform: none; letter-spacing: 0; border-color: #FF120B; color: #FF120B;"
				class="mb-2"
				@click="openManualPaymentModal"
			>
				<v-icon small left style="color: #FF120B;">mdi-pencil-outline</v-icon>
				Registrar pago manual
			</v-btn>
			<v-btn
				block
				small
				depressed
				:disabled="isSendingRequest || clp_amount <= 0"
				:loading="isSendingRequest"
				style="text-transform: none; letter-spacing: 0; background-color: #FF120B;color: white;"
				@click="sendRequest"
			>
				<v-icon small left>mdi-email-fast-outline</v-icon>
				Enviar solicitud al cliente
			</v-btn>
		</div>

		<!-- Detalle del pago (pagado) -->
		<div class="pa-3 white" style="max-width: 300px; min-width: 250px;" v-else>
			<div class="d-flex align-center mb-1" style="gap: 8px;">
				<v-icon small color="green">mdi-check-circle</v-icon>
				<p class="subtitle-2 font-weight-bold mb-0 green--text text--darken-2">Cuota pagada</p>
			</div>
			<v-divider class="my-2"></v-divider>
			<div class="d-flex align-center justify-space-between mb-1">
				<p class="caption grey--text mb-0">Fecha de Pago</p>
				<p class="caption font-weight-bold mb-0">{{ lastPaymentInfo && lastPaymentInfo.payment_date | toDate }}</p>
			</div>
			<div class="d-flex align-center justify-space-between mb-1">
				<p class="caption grey--text mb-0">Monto Pagado</p>
				<p class="caption font-weight-bold mb-0">${{ lastPaymentInfo && lastPaymentInfo.amount | toThousandFilter }}</p>
			</div>
			<div class="d-flex align-center justify-space-between mb-1">
				<p class="caption grey--text mb-0">Forma de Pago</p>
				<p class="caption font-weight-bold mb-0">{{ lastPaymentInfo && lastPaymentInfo.method }}</p>
			</div>
			<v-divider class="my-2"></v-divider>
			<v-btn
				block
				x-small
				outlined
				style="text-transform: none; letter-spacing: 0; color: #524D4D; border-color: #524D4D;"
				@click="openManualPaymentModal"
			>
				<v-icon x-small left>mdi-plus</v-icon>
				Registrar otro pago
			</v-btn>
		</div>
	</v-menu>
</template>

<script>
import { sendSupervisionPaymentRequest } from '@/helpers/api/general'

export default {
	name: 'RequestPaymentSelection',
	props: {
		quota: {
			type: Object,
			required: true
		},
		clp_amount: {
			type: Number,
			required: true
		},
		uf_amount: {
			type: Number,
			required: true
		},
		project: {
			type: Object,
			required: true
		},
	},
	data() {
		return {
			isSendingRequest: false,
		}
	},
	computed: {
		isSubcontractor() {
			return this.$store.state.user.companyPermissions?.subcontractor === true
		},
		paidAmount() {
			const orders = this.quota?.payments
			if (!Array.isArray(orders)) return 0
			return orders.reduce((acc, po) => acc + (po.payment_info ? (Number(po.payment_info.amount) || 0) : 0), 0)
		},
		isFullyPaid() {
			return this.paidAmount > 0 && this.paidAmount >= this.clp_amount
		},
		isPartiallyPaid() {
			return this.paidAmount > 0 && !this.isFullyPaid
		},
		paymentStatus() {
			return this.isFullyPaid
		},
		lastPaymentInfo() {
			const orders = this.quota?.payments
			if (Array.isArray(orders) && orders.length) {
				const paid = orders.filter(po => po.payment_info)
				if (paid.length) return paid[paid.length - 1].payment_info
			}
			return null
		},
		btnStyle() {
			if (this.isFullyPaid) {
				return 'background-color: #2e7d32; color: white !important; pointer-events: auto !important; opacity: 1 !important; justify-content: flex-start; padding: 0 10px;'
			}
			if (this.isPartiallyPaid) {
				return 'color: #2e7d32 !important; border-color: #2e7d32 !important; pointer-events: auto !important; opacity: 1 !important; justify-content: flex-start; padding: 0 10px;'
			}
			if (this.isSubcontractor) {
				return 'background-color: #9E9E9E; color: white !important; pointer-events: auto !important; opacity: 1 !important; justify-content: flex-start; padding: 0 10px;'
			}
			return 'background-color: #FF120B; color: white !important; pointer-events: auto !important; opacity: 1 !important; justify-content: flex-start; padding: 0 10px;'
		},
		tooltipText() {
			if (this.isFullyPaid) return 'Cuota Pagada'
			if (this.isPartiallyPaid) return 'Cuota Parcial'
			if (this.isSubcontractor) return 'Sin Pago Registrado'
			return 'Solicitar Pago'
		},
		tooltipIcon() {
			if (this.isFullyPaid) return 'mdi-check-circle'
			if (this.isPartiallyPaid) return 'mdi-clock-check-outline'
			return 'mdi-cash-clock'
		},
	},
	methods: {
		openManualPaymentModal() {
			const orderData = {
				amount: Math.round(this.clp_amount),
				amount_uf: this.uf_amount,
				payer_id: this.$store.state.user?.userInfo?.id || null,
				concept: `Pago Cuota Supervisión — ${this.quota.name || 'Sin nombre'}`,
				item_id: this.quota.id,
				item_type: 'supervision_payment',
				method: 'Transferencia',
				payment_date: new Date().toISOString().split('T')[0],
			}
			this.$store.commit('general/setManualPaymentOrderModal', {
				orderData,
				status: true
			})
		},
		async sendRequest() {
			this.isSendingRequest = true
			try {
				const response = await sendSupervisionPaymentRequest({
					supervision_payment_id: this.quota.id,
					amount: Math.max(Math.round(this.clp_amount || 0), 0),
					project_id: this.project?.id || null,
				})
				if (response && response.code === 200) {
					this.setMessage({
						message: 'Solicitud de pago enviada al cliente correctamente.',
						title: 'Solicitud enviada',
						type: 'success',
						icon: 'mdi-check-circle',
						color: '#22C55E',
					})
				} else {
					this.setMessage({
						message: 'No fue posible enviar la solicitud. Intenta nuevamente.',
						title: 'Error al enviar',
						type: 'error',
						icon: 'mdi-alert-circle-outline',
						color: 'red',
					})
				}
			} catch {
				this.setMessage({
					message: 'Hubo un error al enviar la solicitud de pago.',
					title: 'Error al enviar',
					type: 'error',
					icon: 'mdi-alert-circle-outline',
					color: 'red',
				})
			} finally {
				this.isSendingRequest = false
			}
		},
		setMessage(messageData) {
			this.$store.dispatch('general/addMessage', {
				message: messageData.message,
				title: messageData.title,
				created: new Date(),
				type: messageData.type,
				icon: messageData.icon,
				color: messageData.color,
			})
		},
	},
}
</script>
